import { useCallback, useRef } from 'react';
import { useNLPCache } from '../context/NLPCacheContext';
import { useNLPLogs } from '../context/NLPLogsContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useNLPProcessing = () => {
  const { getCached, setCached, setLoading } = useNLPCache();
  const { addLog, removeLog } = useNLPLogs();
  const timeoutIdsRef = useRef([]);
  const processingStartedRef = useRef(false);

  // Generic fetch for TF-IDF
  const fetchTFIDF = useCallback(async (tab, texts, force = false) => {
    // Skip if already cached (unless force=true)
    if (!force && getCached(tab, 'tfidf')) {
      console.log(`✅ TF-IDF for ${tab} already cached, skipping`);
      return;
    }

    const logId = addLog(`📊 TF-IDF Analysis - Processing ${texts.length} ${tab}...`, 'loading', tab);
    setLoading(tab, 'tfidf', true);

    try {
      const token = localStorage.getItem('transcend_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/api/v1/analytics/nlp/tfidf`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ texts, top_n: 30 })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCached(tab, 'tfidf', data);
      addLog(`✅ TF-IDF complete: ${data.length} terms extracted (${tab})`, 'success', tab, logId);
      setTimeout(() => removeLog(logId), 3000);
    } catch (error) {
      console.error(`TF-IDF error for ${tab}:`, error);
      addLog(`❌ TF-IDF error: ${error.message} (${tab})`, 'error', tab, logId);
      setTimeout(() => removeLog(logId), 5000);
    } finally {
      setLoading(tab, 'tfidf', false);
    }
  }, [getCached, setCached, setLoading, addLog, removeLog]);

  // Generic fetch for NER
  const fetchNER = useCallback(async (tab, texts, force = false) => {
    // Skip if already cached (unless force=true)
    if (!force && getCached(tab, 'ner')) {
      console.log(`✅ NER for ${tab} already cached, skipping`);
      return;
    }

    const logId = addLog(`🏷️ Entity Extraction - Analyzing ${texts.length} ${tab}...`, 'loading', tab);
    setLoading(tab, 'ner', true);

    try {
      const token = localStorage.getItem('transcend_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/api/v1/analytics/nlp/ner`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ texts })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCached(tab, 'ner', data);
      addLog(`✅ Entity extraction complete: ${data.length} entities found (${tab})`, 'success', tab, logId);
      setTimeout(() => removeLog(logId), 3000);
    } catch (error) {
      console.error(`NER error for ${tab}:`, error);
      addLog(`❌ Entity extraction error: ${error.message} (${tab})`, 'error', tab, logId);
      setTimeout(() => removeLog(logId), 5000);
    } finally {
      setLoading(tab, 'ner', false);
    }
  }, [getCached, setCached, setLoading, addLog, removeLog]);

  // Generic fetch for Topics
  const fetchTopics = useCallback(async (tab, texts, force = false) => {
    // Skip if already cached (unless force=true)
    if (!force && getCached(tab, 'topics')) {
      console.log(`✅ Topics for ${tab} already cached, skipping`);
      return;
    }

    const logId = addLog(`🎯 Topic Discovery - Finding themes in ${texts.length} ${tab}...`, 'loading', tab);
    setLoading(tab, 'topics', true);

    try {
      const token = localStorage.getItem('transcend_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/api/v1/analytics/nlp/topics`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ texts, n_topics: 5, top_words: 8 })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCached(tab, 'topics', data);
      addLog(`✅ Topic discovery complete: ${data.length} themes found (${tab})`, 'success', tab, logId);
      setTimeout(() => removeLog(logId), 3000);
    } catch (error) {
      console.error(`Topics error for ${tab}:`, error);
      addLog(`❌ Topic discovery error: ${error.message} (${tab})`, 'error', tab, logId);
      setTimeout(() => removeLog(logId), 5000);
    } finally {
      setLoading(tab, 'topics', false);
    }
  }, [getCached, setCached, setLoading, addLog, removeLog]);

  // Start background processing for all tabs
  const startBackgroundProcessing = useCallback((allNLPData) => {
    // Prevent duplicate processing
    if (processingStartedRef.current) {
      console.log('⚠️ Background NLP processing already started, skipping');
      return;
    }

    processingStartedRef.current = true;
    console.log('🚀 Starting background NLP processing for all tabs');

    // Clear any existing timeouts
    timeoutIdsRef.current.forEach(id => clearTimeout(id));
    timeoutIdsRef.current = [];

    const tabs = ['deals', 'companies', 'investors'];

    tabs.forEach((tab, tabIndex) => {
      const tabData = allNLPData[tab];
      if (!tabData || !tabData.sourceTexts || tabData.sourceTexts.length === 0) {
        console.log(`⏭️ Skipping ${tab} - no source texts`);
        return;
      }

      const delay = tabIndex * 500; // Stagger: 0ms, 500ms, 1000ms

      // Schedule TF-IDF
      const tfidfTimeout = setTimeout(() => {
        fetchTFIDF(tab, tabData.sourceTexts);
      }, delay);
      timeoutIdsRef.current.push(tfidfTimeout);

      // Schedule NER
      const nerTimeout = setTimeout(() => {
        fetchNER(tab, tabData.sourceTexts);
      }, delay + 200);
      timeoutIdsRef.current.push(nerTimeout);

      // Schedule Topics
      const topicsTimeout = setTimeout(() => {
        fetchTopics(tab, tabData.sourceTexts);
      }, delay + 400);
      timeoutIdsRef.current.push(topicsTimeout);
    });
  }, [fetchTFIDF, fetchNER, fetchTopics]);

  // Process filtered data for a single tab
  const processFilteredTab = useCallback(async (tab, filteredTexts) => {
    console.log(`🔄 Processing filtered ${tab} with ${filteredTexts.length} texts...`);

    // Store original data with '_full' suffix before overwriting (only if not already backed up)
    const currentCached = getCached(tab, 'tfidf');
    const fullBackup = getCached(`${tab}_full`, 'tfidf');
    if (currentCached && !fullBackup) {
      console.log(`💾 Backing up original TF-IDF for ${tab}`);
      setCached(`${tab}_full`, 'tfidf', currentCached);
    }

    const currentNER = getCached(tab, 'ner');
    const fullNERBackup = getCached(`${tab}_full`, 'ner');
    if (currentNER && !fullNERBackup) {
      console.log(`💾 Backing up original NER for ${tab}`);
      setCached(`${tab}_full`, 'ner', currentNER);
    }

    const currentTopics = getCached(tab, 'topics');
    const fullTopicsBackup = getCached(`${tab}_full`, 'topics');
    if (currentTopics && !fullTopicsBackup) {
      console.log(`💾 Backing up original Topics for ${tab}`);
      setCached(`${tab}_full`, 'topics', currentTopics);
    }

    // Process filtered data with FORCE flag to override cache check
    console.log(`🚀 Forcing NLP refresh for ${tab} with ${filteredTexts.length} filtered texts`);
    await Promise.all([
      fetchTFIDF(tab, filteredTexts, true),   // force=true
      fetchNER(tab, filteredTexts, true),     // force=true
      fetchTopics(tab, filteredTexts, true)   // force=true
    ]);

    console.log(`✅ Filtered ${tab} processing complete - cache updated`);
  }, [getCached, setCached, fetchTFIDF, fetchNER, fetchTopics]);

  // Restore full dataset results for a tab
  const restoreFullResults = useCallback((tab) => {
    console.log(`⏮️ Restoring full results for ${tab}...`);

    const fullTFIDF = getCached(`${tab}_full`, 'tfidf');
    if (fullTFIDF) {
      setCached(tab, 'tfidf', fullTFIDF);
    }

    const fullNER = getCached(`${tab}_full`, 'ner');
    if (fullNER) {
      setCached(tab, 'ner', fullNER);
    }

    const fullTopics = getCached(`${tab}_full`, 'topics');
    if (fullTopics) {
      setCached(tab, 'topics', fullTopics);
    }

    console.log(`✅ Full results restored for ${tab}`);
  }, [getCached, setCached]);

  // Check if tab has full results backup
  const hasFullBackup = useCallback((tab) => {
    return !!(getCached(`${tab}_full`, 'tfidf') || getCached(`${tab}_full`, 'ner') || getCached(`${tab}_full`, 'topics'));
  }, [getCached]);

  // Cleanup function
  const cleanup = useCallback(() => {
    timeoutIdsRef.current.forEach(id => clearTimeout(id));
    timeoutIdsRef.current = [];
  }, []);

  return {
    startBackgroundProcessing,
    processFilteredTab,
    restoreFullResults,
    hasFullBackup,
    cleanup
  };
};
